import React, { useEffect, useMemo, useRef, useState } from "react";
import { useCurrentUser } from "./hooks/useCurrentUser";
import { getCountryName } from "./utils/i18nCountry";
import { searchCities } from "./api/geo";
import { fetchSports } from "./api/sports";
import { deletePhoto, patchMe, replaceSports, setAvatar, uploadPhoto } from "./api/users";
import "./App.css";

const API_ASSET_BASE =
  (import.meta.env.VITE_API_BASE ?? import.meta.env.VITE_API_BASE_URL ?? "http://localhost:5044").replace(/\/$/, "");

// const FALLBACK_SPORTS = [
//   {
//     id: 1,
//     code: "basketball",
//     name: "Баскетбол",
//     categories: [
//       { id: 101, code: "5x5", name: "5 на 5" },
//       { id: 102, code: "3x3", name: "3 на 3" },
//       { id: 103, code: "1x1", name: "1 на 1" },
//     ],
//   },
//   {
//     id: 2,
//     code: "boxing",
//     name: "Бокс",
//     categories: [
//       { id: 201, code: "sparring_light", name: "Лёгкие спарринги" },
//       { id: 202, code: "pads", name: "Лапы / пэды" },
//     ],
//   },
// ];

function clampInt(value, min, max) {
  if (value === "" || value === null || value === undefined) return "";
  const n = Number(value);
  if (Number.isNaN(n)) return "";
  return Math.max(min, Math.min(max, Math.trunc(n)));
}

function StepSlide({ step, activeStep, direction, children }) {
  const state = step === activeStep ? "active" : step < activeStep ? "left" : "right";
  return <div className={`step-slide ${state} ${direction > 0 ? "dir-next" : "dir-back"}`}>{children}</div>;
}

/** dropdown + поиск */
function SearchDropdown({
  label,
  required,
  value,
  placeholder,
  inputValue,
  onInputValue,
  items,
  renderItem,
  onPick,
  isOpen,
  setOpen,
  maxHeight = 220,
}) {
  const ref = useRef(null);

  useEffect(() => {
    function onDoc(e) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [setOpen]);

  return (
    <div className="dropdown" ref={ref}>
      <label className={`label ${required ? "req" : ""}`}>{label}</label>
      <input
        className="field"
        value={isOpen ? inputValue : value}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          onInputValue(e.target.value);
          setOpen(true);
        }}
      />

      {isOpen ? (
        <div className="menu">
          <div className="menu-list" style={{ maxHeight }}>
            {items.length === 0 ? (
              <div className="menu-item">
                <span>Ничего не найдено</span>
              </div>
            ) : (
              items.map((it) => (
                <div
                  key={it.__key}
                  className="menu-item"
                  onClick={() => {
                    onPick(it);
                    setOpen(false);
                  }}
                >
                  {renderItem(it)}
                </div>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function App() {
  // ✅ hooks всегда выше любых return
  const { loading, user, isTelegram, error, login, continueAsDev, refreshUser } = useCurrentUser();

  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState("");

  const [sports, setSports] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [avatarUrl, setAvatarUrlState] = useState(null);

  const [form, setForm] = useState({
    displayName: "",
    countryCode: "RU",
    city: "",
    heightCm: "",
    weightKg: "",
    description: "",
  });

  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");

  // Город dropdown
  const [cityQuery, setCityQuery] = useState("");
  const [cityOpen, setCityOpen] = useState(false);
  const [cityOptions, setCityOptions] = useState([]);

  // Спорт: последовательный выбор
  const [sportOpen, setSportOpen] = useState(false);
  const [sportQuery, setSportQuery] = useState("");
  const [pickedSport, setPickedSport] = useState(null);

  const [catOpen, setCatOpen] = useState(false);
  const [catQuery, setCatQuery] = useState("");
  const [pickedCategory, setPickedCategory] = useState(null);

  const [pickedSkill, setPickedSkill] = useState(3);

  // [{ categoryId, skillLevel, sportName, categoryName }]
  const [selected, setSelected] = useState([]);

  const [sportsFromApi, setSportsFromApi] = useState(false);
  const [sportsError, setSportsError] = useState("");

  useEffect(() => {
    setSportsError("");
    fetchSports("")
      .then((items) => {
        if (!Array.isArray(items) || items.length === 0) {
          setSports([]);
          setSportsError("Каталог спорта не загрузился (пустой ответ). Проверь API/туннель.");
          return;
        }
        setSports(items);
      })
      .catch((e) => {
        console.error(e);
        setSports([]);
        setSportsError("Каталог спорта не загрузился. Проверь API/туннель.");
      });
  }, []);
  
  useEffect(() => {
    if (!user) return;

    setHeight(user.heightCm ? String(user.heightCm) : "");
    setWeight(user.weightKg ? String(user.weightKg) : "");

    setForm({
      displayName: user.displayName ?? "",
      countryCode: user.countryCode ?? "RU",
      city: user.city ?? "",
      heightCm: user.heightCm ?? "",
      weightKg: user.weightKg ?? "",
      description: user.description ?? "",
    });

    setPhotos(user.photos ?? []);
    setAvatarUrlState(user.avatarUrl ?? null);

    const rebuilt = (user.sports ?? []).map((s) => ({
      categoryId: s.categoryId,
      skillLevel: s.skillLevel,
      sportName: s.sportName ?? "Спорт",
      categoryName: s.categoryName ?? "Категория",
    }));
    setSelected(rebuilt);
  }, [user]);

  const countryLabel = useMemo(() => getCountryName(form.countryCode, "ru"), [form.countryCode]);

  async function loadCities(q) {
    const text = q.trim();
    if (text.length < 2) {
      setCityOptions([]);
      return;
    }
    try {
      const res = await searchCities({ countryCode: form.countryCode, q: text });
      const mapped = (res ?? []).map((c) => ({
        ...c,
        __key: `${c.countryCode}-${c.name}-${c.region ?? ""}`,
      }));
      setCityOptions(mapped);
    } catch {
      setCityOptions([]);
    }
  }

  const sportItems = useMemo(() => {
    if (!sports?.length) return [];
    const q = sportQuery.trim().toLowerCase();
    const base = (sports ?? []).map((s) => ({ ...s, __key: `sport-${s.id}` }));
    if (!q) return base;
    return base.filter((s) => s.name.toLowerCase().includes(q) || String(s.code ?? "").toLowerCase().includes(q));
  }, [sports, sportQuery]);

  const catItems = useMemo(() => {
    const cats = pickedSport?.categories ?? [];
    const q = catQuery.trim().toLowerCase();
    const base = cats.map((c) => ({ ...c, __key: `cat-${c.id}` }));
    if (!q) return base;
    return base.filter((c) => c.name.toLowerCase().includes(q) || String(c.code ?? "").toLowerCase().includes(q));
  }, [pickedSport, catQuery]);

  function canAddCategory() {
    if (!pickedSport || !pickedCategory) return false;
    const exists = selected.some((x) => Number(x.categoryId) === Number(pickedCategory.id));
    return !exists;
  }

  function addPickedCategory() {
    if (!canAddCategory()) return;

    setSelected((prev) => [
      ...prev,
      {
        categoryId: pickedCategory.id,
        skillLevel: pickedSkill,
        sportName: pickedSport.name,
        categoryName: pickedCategory.name,
      },
    ]);

    // готовим выбор следующего спорта
    setPickedSport(null);
    setPickedCategory(null);
    setSportQuery("");
    setCatQuery("");
    setPickedSkill(3);
  }

  function validateStep0() {
    if (!form.displayName.trim()) return "Заполни имя";
    if (!form.city.trim()) return "Выбери город";
    if (selected.length < 1) return "Выбери минимум одну категорию спорта";
    return "";
  }

  function nextStep() {
    setValidationError("");
    if (step === 0) {
      const err = validateStep0();
      if (err) return setValidationError(err);
    }
    setDirection(1);
    setStep((s) => Math.min(2, s + 1));
  }

  function prevStep() {
    setValidationError("");
    setDirection(-1);
    setStep((s) => Math.max(0, s - 1));
  }

  async function addPhoto(file) {
    try {
      const uploaded = await uploadPhoto(user.id, file);

      if (!uploaded?.id || !uploaded?.url) {
        console.error("Unexpected upload response:", uploaded);
        alert("Сервер вернул странный ответ при загрузке фото. Смотри console.");
        return;
      }

      setPhotos((p) => [...p, uploaded]);
    } catch (e) {
      console.error(e);
      alert("Не удалось загрузить фото. Открой DevTools → Network и посмотри ответ /users/me/photos.");
    }
  }

  async function deletePhotoById(photoId, photoUrl) {
    await deletePhoto(user.id, photoId);
    setPhotos((prev) => prev.filter((x) => x.id !== photoId));
    if (avatarUrl === photoUrl) setAvatarUrlState(null);
  }

  function allSelectedExistInCatalog() {
    const valid = new Set(
      (sports ?? []).flatMap((s) => (s.categories ?? []).map((c) => Number(c.id)))
    );
    return selected.every((x) => valid.has(Number(x.categoryId)));
  }

  async function saveAll() {
    setSaving(true);
    setValidationError("");
    try {
      await patchMe(user.id, {
        displayName: form.displayName,
        countryCode: form.countryCode,
        city: form.city,
        heightCm: form.heightCm === "" ? null : Number(form.heightCm),
        weightKg: form.weightKg === "" ? null : Number(form.weightKg),
        description: form.description,
      });

      const payload = selected.map((x) => ({
        categoryId: Number(x.categoryId),
        skillLevel: Number(x.skillLevel),
      }));

      if (!sportsFromApi) {
        setValidationError("Каталог спорта не загрузился с сервера (соединение/туннель). Попробуй ещё раз.");
        return;
      }

      if (!allSelectedExistInCatalog()) {
        setValidationError("Выбранные категории не совпадают с каталогом сервера. Перевыбери спорт (туннель мог моргнуть).");
        return;
      }
      await replaceSports(user.id, payload);
      await refreshUser(user.id);
    } catch {
      setValidationError("Не удалось сохранить профиль");
    } finally {
      setSaving(false);
    }
  }

  // ✅ returns только после хуков
  if (loading) return <div className="screen"><p>Loading...</p></div>;

  if (!user) {
    return (
      <div className="screen">
        <div className="card">
          <h1 style={{ marginTop: 0 }}>FightDating</h1>
          <p className="helper">Открыл → авторизовался → попал в приложение.</p>
          {isTelegram ? (
            <button className="btn" onClick={login}>Continue with Telegram</button>
          ) : (
            <button className="btn" onClick={continueAsDev}>Continue (dev mode)</button>
          )}
          {error ? <p className="error">{error}</p> : null}
        </div>
      </div>
    );
  }

  const avatarPhoto = avatarUrl ? `${API_ASSET_BASE}${avatarUrl}` : null;

  return (
    <div className="screen">
      <div className="card wizard">
        <h2>Авторизация</h2>
        <p className="subtitle">Шаг {step + 1} из 3</p>

        <div className="slider-frame">
          {/* STEP 0 */}
          <StepSlide step={0} activeStep={step} direction={direction}>
            <div className="step-content">
              <label className="label req">Имя</label>
              <input
                className="field"
                value={form.displayName}
                placeholder="Как к тебе обращаться"
                onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
              />

              <label className="label req">Страна</label>
              <select
                className="field"
                value={form.countryCode}
                onChange={(e) => setForm((f) => ({ ...f, countryCode: e.target.value }))}
              >
                <option value="RU">Россия</option>
              </select>
              <div className="helper">{countryLabel}</div>

              <SearchDropdown
                label="Город"
                required
                value={form.city}
                placeholder="Начни вводить (мин. 2 символа)"
                inputValue={cityQuery}
                onInputValue={(v) => {
                  setCityQuery(v);
                  loadCities(v);
                }}
                items={cityOptions}
                renderItem={(c) => (
                  <>
                    <strong>{c.name}</strong>
                    {c.region ? <span>{c.region}</span> : <span>{c.countryCode}</span>}
                  </>
                )}
                onPick={(c) => {
                  setForm((f) => ({ ...f, city: c.name }));
                  setCityQuery("");
                  setCityOptions([]);
                }}
                isOpen={cityOpen}
                setOpen={(v) => {
                  setCityOpen(v);
                  if (!v) setCityQuery("");
                }}
              />

              <div className="row2">
                <div>
                  <label className="label">Рост (см)</label>
                  <input
                    className="field"
                    type="text"
                    inputMode="numeric"
                    value={height}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, "");
                      setHeight(v);
                    }}
                    onBlur={() => {
                      if (!height) {
                        setForm((f) => ({ ...f, heightCm: "" }));
                        return;
                      }
                      let val = Number(height);
                      if (val < 80) val = 80;
                      if (val > 250) val = 250;
                      const s = String(val);
                      setHeight(s);
                      setForm((f) => ({ ...f, heightCm: s }));
                    }}
                  />
                  <div className="helper">80–250</div>
                </div> 
                <div>
                  <label className="label">Вес (кг)</label>
                  <input
                    className="field"
                    type="text"
                    inputMode="numeric"
                    value={weight}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, "");
                      setWeight(v);
                    }}
                    onBlur={() => {
                      if (!weight) {
                        setForm((f) => ({ ...f, weightKg: "" }));
                        return;
                      }
                      let val = Number(weight);
                      if (val < 20) val = 20;
                      if (val > 300) val = 300;
                      const s = String(val);
                      setWeight(s);
                      setForm((f) => ({ ...f, weightKg: s })); 
                    }}
                  />
                  <div className="helper">20–300</div>
                </div>
              </div>

              <SearchDropdown
                label="Спорт"
                required
                value={pickedSport?.name ?? ""}
                placeholder="Начни вводить спорт"
                inputValue={sportQuery}
                onInputValue={setSportQuery}
                items={sportItems}
                renderItem={(s) => (
                  <>
                    <strong>{s.name}</strong>
                    <span>{(s.categories?.length ?? 0)} категорий</span>
                  </>
                )}
                onPick={(s) => {
                  setPickedSport(s);
                  setPickedCategory(null);
                  setCatQuery("");
                }}
                isOpen={sportOpen}
                  setOpen={(v) => {
                    if (!sports.length) return; // ✅ не открываем
                    setSportOpen(v);
                  }}
              />

              {pickedSport ? (
                <>
                  <SearchDropdown
                    label="Категория"
                    required
                    value={pickedCategory?.name ?? ""}
                    placeholder={`Категория для: ${pickedSport.name}`}
                    inputValue={catQuery}
                    onInputValue={setCatQuery}
                    items={catItems}
                    renderItem={(c) => (
                      <>
                        <strong>{c.name}</strong>
                        {c.code ? <span>{c.code}</span> : <span>&nbsp;</span>}
                      </>
                    )}
                    onPick={setPickedCategory}
                    isOpen={catOpen}
                    setOpen={setCatOpen}
                  />

                  <label className="label">Уровень</label>
                  <select className="field" value={pickedSkill} onChange={(e) => setPickedSkill(Number(e.target.value))}>
                    {[1, 2, 3, 4, 5].map((lvl) => (
                      <option key={lvl} value={lvl}>
                        Уровень {lvl}
                      </option>
                    ))}
                  </select>

                  <div className="btnRow">
                    <button className="btn" type="button" disabled={!canAddCategory()} onClick={addPickedCategory}>
                      Добавить спорт
                    </button>
                    <button
                      className="btn secondary"
                      type="button"
                      onClick={() => {
                        setPickedSport(null);
                        setPickedCategory(null);
                        setSportQuery("");
                        setCatQuery("");
                        setPickedSkill(3);
                      }}
                    >
                      Сброс
                    </button>
                  </div>
                </>
              ) : null}

              {selected.length > 0 ? (
                <div className="pills">
                  {selected.map((x) => (
                    <div key={`${x.categoryId}`} className="pill">
                      <div>
                        <b>{x.sportName}</b> <small>· {x.categoryName} · lvl {x.skillLevel}</small>
                      </div>
                      <button
                        type="button"
                        title="Удалить"
                        onClick={() => setSelected((p) => p.filter((k) => Number(k.categoryId) !== Number(x.categoryId)))}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </StepSlide>

          {/* STEP 1 */}
          <StepSlide step={1} activeStep={step} direction={direction}>
            <div className="step-content">
              <label className="label">Аватар</label>
              <div className="avatarBox">
                {avatarPhoto ? <img src={avatarPhoto} alt="avatar" /> : <div className="muted">Нажми на фото ниже, чтобы поставить аватар</div>}
              </div>

              <label className="label">Фото</label>
              <div className="grid">
                {photos.map((p) => {
                  const isAvatar = avatarUrl === p.url;
                  return (
                    <div key={p.id} className="cell" title="Нажми — сделать аватаром">
                      <img
                        src={`${API_ASSET_BASE}${p.url}`}
                        alt="photo"
                        onClick={async () => {
                          try {
                            await setAvatar(user.id, p.id);
                            setAvatarUrlState(p.url);
                          } catch (e) {
                            console.error(e);
                            alert("Не удалось поставить аватар.");
                          }
                        }}
                      />

                      {isAvatar ? <div className="badge">Аватар</div> : null}

                      <button
                        className="x"
                        type="button"
                        title="Удалить фото"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await deletePhotoById(p.id, p.url);
                          } catch (err) {
                            console.error(err);
                            alert("Не удалось удалить фото.");
                          }
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}

                {photos.length < 5 ? (
                  <label key="add" className="cell" title="Добавить фото">
                    <span className="plus">＋</span>
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        e.target.value = ""; // ✅ критично: можно выбрать тот же файл снова
                        if (!file) return;
                        await addPhoto(file);
                      }}
                    />
                  </label>
                ) : null}
              </div>

              <div className="helper" style={{ marginTop: 10 }}>
                Тап по фото — ставит аватар. Крестик — удаляет. (Макс. 5 фото)
              </div>
            </div>
          </StepSlide>

          {/* STEP 2 */}
          <StepSlide step={2} activeStep={step} direction={direction}>
            <div className="step-content">
              <label className="label">О себе</label>
              <textarea
                className="field about"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Опыт, формат, что ищешь (спарринг/игра), удобное время, район и т.п."
              />
              <div className="helper">Поле можно оставить пустым.</div>
            </div>
          </StepSlide>
        </div>

        {validationError ? <div className="error">{validationError}</div> : null}

        <div className="actions">
          {step > 0 ? (
            <button className="btn secondary" onClick={prevStep}>Назад</button>
          ) : (
            <span />
          )}

          {step < 2 ? (
            <button className="btn" onClick={nextStep}>Далее</button>
          ) : (
            <button className="btn" onClick={saveAll} disabled={saving}>
              {saving ? "Сохранение..." : "Завершить"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}